import { useState, type FormEvent } from 'react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import type { WebhookEventInfo } from '../../lib/api/operatorConsole';

interface Props {
  availableEvents: WebhookEventInfo[];
  onSubmit: (endpoint: {
    url: string;
    description?: string;
    event_types: string[];
  }) => Promise<boolean>;
}

/**
 * Add a destination. The event list is whatever the backend registry says it
 * is — nothing here is hard-coded, so a new event becomes a new checkbox with
 * no change on this side.
 */
export function EndpointForm({ availableEvents, onSubmit }: Props) {
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [events, setEvents] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  function toggle(type: string) {
    setEvents((current) =>
      current.includes(type) ? current.filter((t) => t !== type) : [...current, type],
    );
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!url.trim() || submitting) return;

    setSubmitting(true);
    // Built up rather than spread with `undefined`: the project runs
    // exactOptionalPropertyTypes, so an absent field must really be absent.
    const payload: { url: string; description?: string; event_types: string[] } = {
      url: url.trim(),
      event_types: events,
    };
    const label = description.trim();
    if (label) payload.description = label;

    const ok = await onSubmit(payload);
    setSubmitting(false);
    if (ok) {
      setUrl('');
      setDescription('');
      setEvents([]);
    }
  }

  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      className="mb-6 max-w-2xl rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-4"
    >
      <h2 className="mb-3 text-[14px] font-semibold text-[var(--co-text)]">Add an endpoint</h2>

      <label className="mb-1 block text-[12px] text-[var(--co-muted)]" htmlFor="webhook-url">
        HTTPS URL
      </label>
      <Input
        id="webhook-url"
        type="url"
        value={url}
        placeholder="https://your-backend.com/repackz/events"
        onChange={(e) => setUrl(e.target.value)}
        className="mb-4"
      />

      <label className="mb-1 block text-[12px] text-[var(--co-muted)]" htmlFor="webhook-description">
        Label (optional)
      </label>
      <Input
        id="webhook-description"
        value={description}
        placeholder="Production wallet"
        onChange={(e) => setDescription(e.target.value)}
        className="mb-4"
      />

      <div className="mb-2 text-[12px] text-[var(--co-muted)]">Events to send here</div>
      <div className="flex flex-col gap-2">
        {availableEvents.map((event) => (
          <label key={event.type} className="flex items-start gap-2 text-[12px]">
            <input
              type="checkbox"
              checked={events.includes(event.type)}
              onChange={() => toggle(event.type)}
              disabled={submitting}
              className="mt-[2px]"
            />
            <span>
              <code className="text-[var(--co-text)]">{event.type}</code>
              <span className="block text-[11px] text-[var(--co-muted)]">{event.description}</span>
            </span>
          </label>
        ))}
      </div>

      <p className="mt-3 text-[11px] text-[var(--co-muted)]">
        We sign every request the same way as the wallet callbacks, and expect any 2xx back. Nothing
        in your reply is read.
      </p>

      <div className="mt-4">
        <Button type="submit" disabled={submitting || !url.trim()}>
          {submitting ? 'Adding…' : 'Add endpoint'}
        </Button>
      </div>
    </form>
  );
}
